import { RefObject, useEffect, useRef, useState } from 'react';
import { useContentRef } from '~/routes/($code)._index/body/main-v2/slides/common/homepage-slide';
import { SwiperClass, useSwiper } from 'swiper/react';

interface IUseMotionProps {
  MOTION_REAL_HEIGHT: number;
  MOTION_REAL_WIDTH: number;

  /**
   * load image when sliding to index xxx
   */
  loading: number;
}

interface IUseMotionResult {
  motionRef: RefObject<HTMLDivElement>;
  showImage: boolean;
}

export const useMotion = (props: IUseMotionProps): IUseMotionResult => {
  const { MOTION_REAL_WIDTH, MOTION_REAL_HEIGHT, loading } = props;
  const contentRef = useContentRef();
  const motionRef = useRef<HTMLDivElement>(null);

  const [showImage, setShowImage] = useState(false);

  const swiper = useSwiper();

  // calc size
  useEffect(() => {
    const $content = contentRef.current;
    const $motion = motionRef.current;
    if (!$motion || !$content) {
      return;
    }
    const $motionLayer = $motion.children.item(0) as HTMLDivElement;

    const observer = new ResizeObserver(() => {
      const height = $motion.offsetHeight;
      const width = (height / MOTION_REAL_HEIGHT) * MOTION_REAL_WIDTH;

      $motion.style.width = `${width}px`;
      $motionLayer.style.transform = `scale(${height / MOTION_REAL_HEIGHT})`;
    });

    observer.observe($content);

    return () => {
      observer.disconnect();
    };
  }, []);

  // auto play & replay
  useEffect(() => {
    const $motion = motionRef.current;
    const $content = contentRef.current;

    if (!$motion || !$content) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          $motion.classList.add('anime');
        } else {
          $motion.classList.remove('anime');
        }
      },
      {
        rootMargin: '-10px',
      },
    );

    observer.observe($content);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (loading === 0) {
      setShowImage(true);
      return;
    }

    if (!swiper || !loading || showImage) {
      return;
    }

    const handleSlideChange = (swiper: SwiperClass): void => {
      if (swiper.activeIndex >= loading) {
        setShowImage(true);
      }
    };

    swiper.on('slideChange', handleSlideChange);

    return () => {
      swiper.off('slideChange', handleSlideChange);
    };
  }, [loading, swiper, showImage]);

  return {
    motionRef,
    showImage,
  };
};
